import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { stashHandoff } from "../copilot_native/hooks/use-copilot";
import { isCopilotConfigured } from "../copilot_native/api/copilot-api";
import { copilotPathFor } from "../copilot_native/model/copilot-paths";
import Button from "../../components/button/button";
import {
    HiOutlineArrowTopRightOnSquare,
    HiOutlineCalendarDays,
    HiOutlineCheck,
    HiOutlineClipboard,
    HiOutlineExclamationCircle,
    HiOutlineExclamationTriangle,
    HiOutlineInformationCircle,
    HiOutlineSparkles,
    HiOutlineWrenchScrewdriver,
    HiChevronDown,
} from "react-icons/hi2";

// Surfaces, aligned with PageContainer (white / slate-950).
export const CARD = 'rounded-xl border border-gray-200 bg-white';
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500';

// Severity is a status, so it always ships with an icon and a word -- never
// colour alone.
const SEVERITY_META = {
    high: {
        label: 'High impact',
        icon: HiOutlineExclamationCircle,
        className: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30',
    },
    medium: {
        label: 'Medium impact',
        icon: HiOutlineExclamationTriangle,
        className: 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
    },
    low: {
        label: 'Low impact',
        icon: HiOutlineInformationCircle,
        className: 'bg-gray-50 text-gray-700 ring-gray-500/20',
    },
};

// Where in the agent each root cause gets fixed, in builder terms.
const CATEGORY_HINTS = {
    instructions: 'Instructions are missing a rule, are unclear, or were not followed.',
    knowledge: 'The knowledge base did not have, or did not return, the answer.',
    tool: 'A tool or API failed, returned the wrong data, or was used wrongly.',
    mcp: 'A tool on a connected MCP server failed or was used wrongly.',
    skill: 'A skill gave wrong or incomplete guidance.',
    workflow: 'A playbook or workflow did not start, looped, or ended early.',
    guardrail: 'A guardrail blocked a legitimate request.',
    settings: 'The model, API key or provider settings were rejected.',
};

// How a conversation ended. Status colours, always shown with a label and a
// count in the legend.
export const OUTCOMES = [
    { key: 'resolved', label: 'Resolved', swatch: 'bg-emerald-500' },
    { key: 'partially_resolved', label: 'Partly resolved', swatch: 'bg-brand-lime' },
    { key: 'escalated', label: 'Escalated', swatch: 'bg-amber-400' },
    { key: 'unresolved', label: 'Unresolved', swatch: 'bg-rose-500' },
    { key: 'abandoned', label: 'Abandoned', swatch: 'bg-gray-500' },
    { key: 'out_of_scope', label: 'Out of scope', swatch: 'bg-gray-300' },
];

export const LAST_30 = 'last30';
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;
const MAX_MONTHS_BACK = 12;

export function formatTime(at) {
    if (!at) return null;
    const when = new Date(at);
    return Number.isNaN(when.getTime()) ? at : when.toLocaleString();
}

function relativeTime(at) {
    const when = new Date(at).getTime();
    if (Number.isNaN(when)) return '';
    const minutes = Math.round((Date.now() - when) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}

function monthValue(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthsAgo(count) {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - count);
    return monthValue(date);
}

// The range as the API takes it: "last30" or {from, to} months.
export function rangeParam(key) {
    const match = /^m(\d{4}-\d{2})_(\d{4}-\d{2})$/.exec(key || '');
    return match ? { from: match[1], to: match[2] } : LAST_30;
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

const FIELD = 'rounded-lg border border-gray-200 bg-white text-sm text-gray-900 shadow-sm transition '
    + 'hover:border-gray-300 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 disabled:opacity-60 '
    + '';

export function RangePicker({ rangeKey, computedRanges, disabled, onChange }) {
    const custom = rangeKey !== LAST_30 && !computedRanges.some(row => row.key === rangeKey);
    const [choosing, setChoosing] = useState(false);
    const current = rangeParam(rangeKey);
    const [from, setFrom] = useState(current.from || monthsAgo(2));
    const [to, setTo] = useState(current.to || monthsAgo(0));
    const earliest = monthsAgo(MAX_MONTHS_BACK - 1);
    const latest = monthsAgo(0);
    const previous = computedRanges.filter(row => row.key !== LAST_30);
    const invalid = !from || !to || from > to || from < earliest || to > latest;

    const select = value => {
        if (value === 'choose') {
            setChoosing(true);
            return;
        }
        setChoosing(false);
        onChange(value);
    };

    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
                <HiOutlineCalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <select
                    aria-label="Period"
                    value={choosing ? 'choose' : rangeKey}
                    disabled={disabled}
                    onChange={event => select(event.target.value)}
                    className={`${FIELD} py-1.5 pl-9 pr-8 font-medium`}
                >
                    <option value={LAST_30}>Last 30 days</option>
                    {previous.length > 0 && (
                        <optgroup label="Analysed before">
                            {previous.map(row => <option key={row.key} value={row.key}>{row.label}</option>)}
                        </optgroup>
                    )}
                    {custom && !choosing && <option value={rangeKey}>{`${current.from} – ${current.to}`}</option>}
                    <option value="choose">Choose months…</option>
                </select>
            </div>
            {choosing && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 p-1 pl-2">
                    <input type="month" aria-label="From month" value={from} min={earliest} max={to}
                           onChange={event => setFrom(event.target.value)} className={`${FIELD} px-2 py-1`} />
                    <span className="text-xs text-gray-500">to</span>
                    <input type="month" aria-label="To month" value={to} min={from} max={latest}
                           onChange={event => setTo(event.target.value)} className={`${FIELD} px-2 py-1`} />
                    <Button color="primary" size="xs" text="Apply" disabled={invalid}
                            onClick={() => { setChoosing(false); onChange(`m${from}_${to}`); }} />
                    <button type="button" onClick={() => setChoosing(false)}
                            className="px-1 text-xs font-medium text-gray-500 hover:text-gray-900">
                        Cancel
                    </button>
                    {from && from < earliest && (
                        <span className="text-xs text-red-600 dark:text-red-400">Up to {MAX_MONTHS_BACK} months back</span>
                    )}
                </div>
            )}
        </div>
    );
}

/** When the report was computed: the first thing to check when reading it. */
export function LastUpdated({ at, rolling }) {
    if (!at) return null;
    const stale = Date.now() - new Date(at).getTime() > STALE_AFTER_MS;
    return (
        <span title={formatTime(at)} className="inline-flex items-center gap-2 text-xs text-gray-600">
            <span className={`h-2 w-2 shrink-0 rounded-full ${stale ? 'bg-amber-400' : 'bg-emerald-500'}`} aria-hidden="true" />
            <span>
                Updated <span className="font-semibold text-gray-900">{relativeTime(at)}</span>
                <span className="hidden text-gray-400 sm:inline"> · {formatTime(at)}</span>
                {stale && (
                    <span className="ml-1 font-medium text-amber-700 dark:text-amber-300">
                        · {rolling ? 'refreshes tonight' : 'analyse again to refresh'}
                    </span>
                )}
            </span>
        </span>
    );
}

/** One slim line under the toolbar for anything the reader should know first. */
export function Notice({ tone = 'info', children, action }) {
    const tones = {
        info: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200',
        warn: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200',
        error: 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200',
        quiet: 'border-gray-200 bg-gray-50 text-gray-700',
    };
    return (
        <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${tones[tone]}`}>
            <span className="flex items-center gap-2">{children}</span>
            {action}
        </div>
    );
}

export function Spinner() {
    return <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// KPIs and outcomes
// ---------------------------------------------------------------------------

/** A headline number. `meter` (0-100) draws a slim bar under it. */
export function Kpi({ label, value, detail, meter, icon: Icon }) {
    return (
        <div className={`${CARD} p-4 sm:p-5`}>
            <div className="flex items-center justify-between gap-3">
                <dt className={EYEBROW}>{label}</dt>
                {Icon && <Icon className="h-4 w-4 text-gray-400" aria-hidden="true" />}
            </div>
            <dd className="mt-2 text-2xl font-semibold tracking-tight text-gray-900 tabular-nums sm:text-3xl">{value}</dd>
            {typeof meter === 'number' && (
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-gray-900" style={{ width: `${Math.min(Math.max(meter, 0), 100)}%` }} />
                </div>
            )}
            {detail && <p className="mt-2 text-xs text-gray-500">{detail}</p>}
        </div>
    );
}

export function KpiSkeleton() {
    return (
        <div role="status" className={`${CARD} animate-pulse p-5`}>
            <div className="h-2.5 w-24 rounded-full bg-gray-200" />
            <div className="mt-4 h-8 w-20 rounded-lg bg-gray-200" />
            <div className="mt-4 h-1.5 w-full rounded-full bg-gray-100" />
            <span className="sr-only">Loading…</span>
        </div>
    );
}

/** How the analysed conversations ended, as one segmented bar with a legend. */
export function OutcomeBar({ outcomes }) {
    const counts = Object.fromEntries((outcomes || []).map(row => [row.outcome, row.conversations]));
    const rows = OUTCOMES.map(meta => ({ ...meta, count: counts[meta.key] || 0 }));
    const total = rows.reduce((sum, row) => sum + row.count, 0);
    if (!total) return null;
    const shown = rows.filter(row => row.count > 0);

    return (
        <section className={`${CARD} p-5`}>
            <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-sm font-semibold text-gray-900">How conversations ended</h2>
                <span className="text-xs text-gray-500">{total} analysed</span>
            </div>
            <div className="mt-4 flex h-3 w-full gap-0.5 overflow-hidden rounded-full" role="img"
                 aria-label={shown.map(row => `${row.label} ${row.count}`).join(', ')}>
                {shown.map(row => (
                    <div key={row.key} className={`${row.swatch} h-full first:rounded-l-full last:rounded-r-full transition-opacity hover:opacity-80`}
                         style={{ width: `${(row.count / total) * 100}%` }}
                         title={`${row.label}: ${row.count} (${Math.round((row.count / total) * 100)}%)`} />
                ))}
            </div>
            <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                {rows.map(row => (
                    <li key={row.key} className={`flex items-center gap-2 text-sm ${row.count ? '' : 'opacity-40'}`}>
                        <span className={`h-2.5 w-2.5 rounded-sm ${row.swatch}`} aria-hidden="true" />
                        <span className="text-gray-600">{row.label}</span>
                        <span className="font-semibold tabular-nums text-gray-900">{row.count}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Insight panels
// ---------------------------------------------------------------------------

export function Panel({ title, subtitle, children }) {
    return (
        <section className={`${CARD} flex flex-col p-5`}>
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
            <div className="mt-5 flex-1">{children}</div>
        </section>
    );
}

function EmptyPanel({ text }) {
    return (
        <div className="flex h-full min-h-[8rem] flex-col items-center justify-center gap-2 text-center">
            <HiOutlineSparkles className="h-5 w-5 text-gray-300" />
            <p className="text-sm text-gray-500">{text}</p>
        </div>
    );
}

/** Horizontal bars, one series, value in text ink. */
function Bars({ rows }) {
    const max = Math.max(...rows.map(row => row.value), 1);
    return (
        <ul className="space-y-4">
            {rows.map(row => (
                <li key={row.key} title={row.tooltip} className="group">
                    <div className="flex items-baseline justify-between gap-3">
                        <span className="truncate text-sm font-medium text-gray-900">{row.label}</span>
                        <span className="shrink-0 text-xs tabular-nums text-gray-500">{row.valueLabel}</span>
                    </div>
                    <div className="mt-1.5 h-2 w-full rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-gray-900 transition-opacity group-hover:opacity-80"
                             style={{ width: `${Math.max((row.value / max) * 100, 3)}%` }} />
                    </div>
                    {row.hint && <p className="mt-1.5 text-xs text-gray-500">{row.hint}</p>}
                    {row.extra}
                </li>
            ))}
        </ul>
    );
}

export function CategoryPanel({ categories }) {
    return (
        <Panel title="Why conversations fail" subtitle="Share of analysed conversations with a problem in each part of the agent">
            {categories?.length ? (
                <Bars rows={categories.map(row => ({
                    key: row.category,
                    label: row.label,
                    value: row.conversations,
                    valueLabel: `${row.conversations} · ${row.share}%`,
                    hint: CATEGORY_HINTS[row.category],
                    tooltip: `${row.label}: ${row.conversations} conversations, ${row.share}% of analysed`,
                }))} />
            ) : <EmptyPanel text="No problems found in the analysed conversations." />}
        </Panel>
    );
}

function ResolvedChip({ rate }) {
    const tone = rate >= 70
        ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30'
        : rate >= 40
            ? 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30'
            : 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30';
    return (
        <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${tone}`}>
            {rate}% resolved
        </span>
    );
}

export function TopicPanel({ topics }) {
    return (
        <Panel title="What users ask about" subtitle="Most common requests, and how often each is resolved">
            {topics?.length ? (
                <Bars rows={topics.map(row => ({
                    key: row.topic,
                    label: row.topic,
                    value: row.conversations,
                    valueLabel: `${row.conversations} conversation${row.conversations === 1 ? '' : 's'}`,
                    tooltip: `${row.topic}: ${row.conversations} conversations, ${row.resolutionRate}% resolved, ${row.withIssues} with a problem`,
                    extra: <div className="mt-1.5"><ResolvedChip rate={row.resolutionRate} /></div>,
                }))} />
            ) : <EmptyPanel text="No analysed conversations yet." />}
        </Panel>
    );
}

// ---------------------------------------------------------------------------
// What to fix first
// ---------------------------------------------------------------------------

function Chip({ className, children }) {
    return (
        <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${className}`}>
            {children}
        </span>
    );
}

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard blocked; the text is on screen to select by hand.
        }
    };
    return (
        <button type="button" onClick={copy}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 transition hover:bg-white hover:text-gray-900">
            {copied ? <HiOutlineCheck className="h-3.5 w-3.5" /> : <HiOutlineClipboard className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
        </button>
    );
}

// The composer accepts 20,000 characters; this leaves room for the builder to add to it.
const FIX_PROMPT_MAX_CHARS = 8000;

/** The request Copilot gets for one problem: what happened, the suggested fix, and where to look. */
export function buildFixPrompt(issue, appId, env) {
    const live = env === 'live';
    const lines = [
        `Fix this problem that Analytics found in my agent's ${live ? 'Live (published)' : 'Test'} conversations.`,
        // Read by Copilot: its trace tools take this as their `environment`.
        `Environment: ${live ? 'live' : 'test'}`,
        '',
        `Problem: ${issue.title}`,
        `Part of the agent: ${issue.label}${issue.component ? ` (${issue.component})` : ''}`,
        `Affected: ${issue.conversations} conversation${issue.conversations === 1 ? '' : 's'} (${issue.share}% of those analysed)`,
    ];
    if (issue.evidence?.length) {
        lines.push('', 'What happened:');
        issue.evidence.forEach(item => lines.push(`- ${item.text}${item.sessionId ? ` (session ${item.sessionId})` : ''}`));
    }
    if (issue.fix) {
        lines.push('', 'Suggested fix:', issue.fix);
    }
    if (issue.sessions?.length) {
        lines.push('', `Session IDs: ${issue.sessions.join(', ')}`);
        if (live) {
            lines.push(`These sessions are from the published agent, recorded under app id ${appId}_live.`);
        }
    }
    lines.push('', 'Look at these sessions\' traces to confirm the cause, then make the change.'
        + (live ? ' Your changes go to the draft agent; tell me to publish when it is ready.' : ''));
    return lines.join('\n');
}

/** Opens this app's Copilot in a fresh chat, with the fix request ready to review and send. */
function FixWithAiButton({ issue, appId, env }) {
    const navigate = useNavigate();
    if (!isCopilotConfigured()) return null;
    const open = event => {
        event.stopPropagation();
        stashHandoff(appId, buildFixPrompt(issue, appId, env), { newChat: true, maxChars: FIX_PROMPT_MAX_CHARS });
        navigate(copilotPathFor(appId));
    };
    return (
        <button type="button" onClick={open} title="Open Copilot with this problem, its evidence and the suggested fix"
                className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900/30">
            <HiOutlineSparkles className="h-3.5 w-3.5 text-brand-lime" />
            Fix with AI
        </button>
    );
}

export function IssueCard({ issue, appId, env, rank }) {
    const [open, setOpen] = useState(rank === 1);
    const severity = SEVERITY_META[issue.severity] || SEVERITY_META.medium;
    const SeverityIcon = severity.icon;
    const top = rank === 1;

    return (
        <li className={`${CARD} overflow-hidden transition hover:border-gray-300`}>
            <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open}
                    className="flex w-full items-start gap-4 p-4 text-left sm:p-5">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${
                    top ? 'bg-brand-lime text-gray-900' : 'bg-gray-100 text-gray-600'}`}>
                    {rank}
                </span>
                <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900">{issue.title}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <Chip className={severity.className}>
                            <SeverityIcon className="h-3.5 w-3.5" />{severity.label}
                        </Chip>
                        <Chip className="bg-white text-gray-700 ring-gray-200">
                            {issue.label}{issue.component ? ` · ${issue.component}` : ''}
                        </Chip>
                        {/* The count column is hidden on small screens; show it here instead. */}
                        <span className="text-[11px] font-medium text-gray-500 sm:hidden">
                            {issue.conversations} conv{issue.conversations === 1 ? '' : 's'} · {issue.share}%
                        </span>
                    </div>
                </div>
                <div className="hidden w-32 shrink-0 text-right sm:block">
                    <p className="text-lg font-semibold tabular-nums text-gray-900">
                        {issue.conversations}
                        <span className="ml-1 text-xs font-normal text-gray-500">
                            conv{issue.conversations === 1 ? '' : 's'}
                        </span>
                    </p>
                    <div className="mt-1.5 h-1 w-full rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-gray-900" style={{ width: `${Math.min(issue.share, 100)}%` }} />
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">{issue.share}% of analysed</p>
                </div>
                <HiChevronDown className={`mt-1 h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="space-y-5 border-t border-gray-100 bg-gray-50/60 px-4 py-5 sm:px-5 sm:pl-[4.25rem]">
                    {issue.fix && (
                        <div className="rounded-lg border-l-4 border-brand-lime bg-white p-4 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className={`${EYEBROW} flex items-center gap-1.5`}>
                                    <HiOutlineWrenchScrewdriver className="h-4 w-4" /> How to fix
                                </p>
                                <div className="flex items-center gap-1">
                                    <CopyButton text={issue.fix} />
                                    <FixWithAiButton issue={issue} appId={appId} env={env} />
                                </div>
                            </div>
                            <p className="mt-2 text-sm leading-relaxed text-gray-800">{issue.fix}</p>
                        </div>
                    )}

                    {!issue.fix && (
                        <div className="flex justify-end">
                            <FixWithAiButton issue={issue} appId={appId} env={env} />
                        </div>
                    )}

                    {issue.evidence?.length > 0 && (
                        <div>
                            <p className={EYEBROW}>What happened</p>
                            <ul className="mt-2 space-y-2">
                                {issue.evidence.map((item, index) => (
                                    <li key={index} className="border-l-2 border-gray-300 pl-3 text-sm italic leading-relaxed text-gray-700">
                                        {item.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {issue.sessions?.length > 0 && (
                        <div>
                            <p className={EYEBROW}>Example conversations</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {issue.sessions.map(sessionId => (
                                    <Link key={sessionId}
                                          to={`/app/${appId}/traces?session=${encodeURIComponent(sessionId)}&env=${env}`}
                                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1 font-mono text-xs text-gray-700 shadow-sm transition hover:border-gray-900 hover:text-gray-900">
                                        {sessionId}
                                        <HiOutlineArrowTopRightOnSquare className="h-3.5 w-3.5" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </li>
    );
}

export function SectionSkeleton() {
    return (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[0, 1].map(key => (
                <div key={key} className={`${CARD} animate-pulse space-y-5 p-5`}>
                    <div className="h-3 w-40 rounded-full bg-gray-200" />
                    {[0, 1, 2].map(row => (
                        <div key={row} className="space-y-2">
                            <div className="h-2.5 w-1/3 rounded-full bg-gray-200" />
                            <div className="h-2 w-full rounded-full bg-gray-100" />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

export function EmptyState({ title, body, action }) {
    return (
        <div className={`${CARD} flex flex-col items-center px-6 py-14 text-center`}>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-lime/25 text-gray-900 dark:bg-brand-lime/15 dark:text-brand-lime">
                <HiOutlineSparkles className="h-6 w-6" />
            </span>
            <p className="mt-4 text-base font-semibold text-gray-900">{title}</p>
            {body && <p className="mt-1 max-w-md text-sm text-gray-500">{body}</p>}
            {action && <div className="mt-6">{action}</div>}
        </div>
    );
}
